import { useState, useEffect, useMemo } from "react";
import { useMealType, usePlan, useStep } from "../contexts/SubscriptionContext";
import GeneralButton from "../common/GeneralButton";
import { TiChevronLeft, TiChevronRight } from "react-icons/ti";
import { motion } from "framer-motion";
import { useToast } from "../contexts/ToastContext";
import Modal from "../common/Modal";
import { useNavigate } from "react-router-dom";
import axios from "../../config/axios";
import FullScreenLoading from "../common/loading/FullScreenLoading";
import ApiErrorMessage from "../common/ApiErrorMessage";
import NoItemsFound from "../common/NoItemsFound";
const usePlanPageContent = () => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await axios.get("get-plans");
        if (active) setContent(data);
      } catch (err) {
        console.error(err);
        if (active) setError("Failed to load plans. Please try again later.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { content, loading, error };
};

const ChoosePlan = () => {
  const { content, loading, error, discountCoupons } = usePlanPageContent();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  const { goToStep } = useStep();
  const bgColors = ["#A0AE3F", "#295B24", "#30160E", "#D06F36","#adb1abff"];

  const hexToRgba = (hex, opacity = 0.2) => {
    const r = parseInt(hex.substring(1, 3), 16);
    const g = parseInt(hex.substring(3, 5), 16);
    const b = parseInt(hex.substring(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const { selectedPlan, setSelectedPlan } = usePlan();
  const { selectedMealType, setSelectedMealType } = useMealType();
  const { nextStep, prevStep, activeStep } = useStep();
  const saveToLocal = () => {
    localStorage.setItem("selectedPlan", JSON.stringify(selectedPlan));
    localStorage.setItem("selectedMealType", selectedMealType);
  };

  return (
    <div>
      {loading && <FullScreenLoading />}
      {error ? (
        <ApiErrorMessage
          message1="Failed to load plans"
          message2="Please check your connection and try again."
        />
      ) : content.length === 0 ? (
        <NoItemsFound
          message1="No plans available"
          message2="Please check back later."
        />
      ) : (
        <>
          {activeStep === "1" && (
            <motion.div
              initial={{ x: -200, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -200, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <p className="text-30pt xl:text-34pt pb-4 lg:pb-9">
                Choose a plan
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 1xl:grid-cols-4 2.5xl:grid-cols-[1.4fr_1.4fr_1.4fr_1.4fr_0.8fr] gap-8 items-start">
                {content.map((plan, index) => {
                  const bgColor = hexToRgba(
                    bgColors[index % bgColors.length],
                    0.2
                  );
                  const bgColor2 = hexToRgba(
                    bgColors[index % bgColors.length],
                    0.4
                  );
                  const bgColor3 = bgColors[index % bgColors.length];

                  const isSelected = selectedPlan?._id === plan._id;

                  return (
                    <div
                      key={index}
                      title="Click to select plan"
                      className={`cursor-pointer rounded-[15px] flex flex-col items-end ${
                        (index + 1 - 1) % 4 === 0 && index !== 0
                          ? "col-start-1"
                          : ""
                      }`}
                    >
                      <div
                        onClick={() => setSelectedPlan(plan)}
                        className={`cursor-pointer w-full rounded-[15px] ${
                          isSelected ? "mb-9" : ""
                        } ${
                          (index + 1 - 1) % 4 === 0 && index !== 0
                            ? "col-start-1"
                            : ""
                        }`}
                        style={{
                          backgroundColor: isSelected ? bgColor : "transparent", // green with opacity
                          transition:
                            "background-color 0.3s ease, margin 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease",
                        }}
                      >
                        <div
                          className={`rounded-[15px] px-6 lg:px-8 2.5xl:px-12 pt-10 pb-6 relative${
                            isSelected
                              ? ""
                              : "mb-5 hover:shadow-lg hover:scale-105 transform"
                          } `}
                          style={{
                            backgroundColor: isSelected
                              ? "transparent"
                              : bgColor,
                            transition:
                              "background-color 0.3s ease, margin 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease",
                          }}
                        >
                          {plan.specialOfferText && (
                            <p
                              style={{ color: plan.specialOfferTextColor }}
                              className="absolute top-4 2.5xl:left-12 text-14pt md:text-xs font-mono text-secondary"
                            >
                              {plan.specialOfferText}
                            </p>
                          )}
                          <GeneralButton
                            text={plan.priceBadgeText}
                            className=" text-18pt md:mb-3 mb-1 xl:py-1"
                            bgColor={plan.priceBadgeColor}
                            icon={null}
                            textColor={plan.badgeTextColor || "#fff"}
                          />
                          <h2 className="text-40pt 2.5xl:text-48pt leading-none text-nowrap">
                            {plan.title}
                          </h2>
                          <h2 className="text-28pt 2.5xl:text-32pt leading-none">
                            plan
                          </h2>
                          <div
                            className={`w-[54px] h-[54px] transform 0.2s ease rounded-full border-[3px] absolute bottom-5 right-8 bg-[#fff]`}
                          ></div>
                          {isSelected && (
                            <div
                              style={{ backgroundColor: bgColor3 }}
                              className={`w-[35px] h-[35px] transform 0.2s ease rounded-full  absolute bottom-[30px] right-[42px]`}
                            ></div>
                          )}
                        </div>
                        <div
                          className={`rounded-[15px]  ${
                            isSelected ? "py-12 px-4" : " px-4 py-4"
                          }`}
                          style={{
                            backgroundColor: isSelected
                              ? bgColor2
                              : "transparent", // inner overlay
                            transition: "background-color 0.3s ease",
                          }}
                        >
                          <p
                            className={`${
                              isSelected
                                ? "font-bold html w-[80%]"
                                : " w-[90%] 2.5xl:w-[80%]"
                            } text-21pt font-mono  mx-auto`}
                            dangerouslySetInnerHTML={{
                              __html: plan.description,
                            }}
                          ></p>
                        </div>
                      </div>
                      {isSelected && (
                        <GeneralButton
                          onClick={() => {
                            nextStep();
                          }}
                          text={"Next step"}
                          title=""
                          bgColor="#30160E"
                          textColor="#ccc"
                          icon={<TiChevronRight size={24} className="ms-4" />}
                          className="text-21pt 2.5xl:text-24pt"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {activeStep === "1.1" && (
            <motion.div
              className="grid  grid-cols-1 lg:grid-cols-1 xl:grid-cols-[5.5fr_1fr] 2xl:grid-cols-[3.5fr_1fr] 2.5xl:grid-cols-[2.3fr_1fr]"
              initial={{ x: -200, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -200, opacity: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <div className="">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between ">
                  <p className="text-30pt xl:text-34pt pb-0 md:pb-4 lg:pb-9">
                    Choose a plan / meals preference
                  </p>
                  <p className="text-18pt text-quaternary font-mono pb-4  lg:pb-9">
                    5 meals/ week
                  </p>
                </div>

                <div
                  className={`rounded-[15px] grid grid-cols-1 md:grid-cols-[1fr_2fr]  md:h-[200px] mb-9`}
                  style={{
                    backgroundColor: "rgba(41, 91, 36, 0.2)",
                    transition:
                      "background-color 0.3s ease, margin 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease",
                  }}
                >
                  <div
                    className={`rounded-[15px] px-12 pt-10 pb-10  relative md:h-[200px] md:w-[304px]`}
                    style={{
                      backgroundColor: "rgba(41, 91, 36, 0.2)",
                      transition:
                        "background-color 0.3s ease, margin 0.3s ease, transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                  >
                    <GeneralButton
                      text={`${selectedPlan.daysInPlan} days meals`}
                      className=" text-18pt md:mb-3 mb-1 xl:py-1"
                      textColor="#fff"
                      icon={null}
                    />
                    <h2 className="text-40pt md:text-48pt leading-none">
                      {selectedPlan.title}
                    </h2>
                    <h2 className="text-32pt leading-none">plan</h2>
                  </div>

                  <div className="flex flex-col lg:flex-row lg:items-center justify-center lg:justify-around px-12 lg:px-0 py-8 md:py-2">
                    {/* Veg option */}
                    <div
                      className="flex items-center justify-start cursor-pointer pb-3 lg:pb-0"
                      onClick={() => setSelectedMealType("Veg")}
                    >
                      <div className="relative">
                        <div className="w-[34px] h-[34px] transform 0.2s ease rounded-full border-[3px] relative bg-[#fff]"></div>
                        {selectedMealType === "Veg" && (
                          <div className="w-[22px] h-[22px] transform 0.2s ease rounded-full  z-[99] absolute bottom-[6px] left-[6px] bg-[#295B24]"></div>
                        )}
                      </div>
                      <p className="text-24pt whitespace-nowrap md:text-30pt ms-5 text-nowrap">
                        Vegetarian
                      </p>
                    </div>

                    {/* Non-Veg option */}
                    <div
                      className="flex items-center justify-start cursor-pointer"
                      onClick={() => setSelectedMealType("Non Veg")}
                    >
                      <div className="relative">
                        <div className="w-[34px] h-[34px] transform 0.2s ease rounded-full border-[3px] relative bg-[#fff]"></div>
                        {selectedMealType === "Non Veg" && (
                          <div className="w-[22px] h-[22px] transform 0.2s ease rounded-full  z-[99] absolute bottom-[6px] left-[6px] bg-[#295B24]"></div>
                        )}
                      </div>
                      <p className="text-24pt whitespace-nowrap md:text-30pt ms-5 text-nowrap">
                        Non-Vegetarian
                      </p>
                    </div>
                  </div>
                </div>

                {/* Navigation Buttons */}
                <div className="flex flex-col  gap-5 xs:flex-row items-center justify-between">
                  <GeneralButton
                    onClick={() => {
                      if (activeStep === "1") {
                        navigate(-1);
                        prevStep();
                      } else {
                        prevStep();
                      }
                    }}
                    text={"Prev step"}
                    title=""
                    iconPosition="Left"
                    bgColor="#ccc"
                    textColor="#000"
                    icon={<TiChevronLeft size={24} className="me-4" />}
                    className="text-24pt"
                  />
                  <GeneralButton
                    onClick={
                      selectedMealType
                        ? () => {
                            nextStep();
                            saveToLocal();
                            navigate("/subscribe/meals");
                          }
                        : () => setShow(true)
                    }
                    text={"Next step"}
                    title=""
                    bgColor="#30160E"
                    textColor="#ccc"
                    icon={<TiChevronRight size={24} className="ms-4" />}
                    className="text-24pt"
                  />
                </div>
              </div>
            </motion.div>
          )}
          <Modal
            isOpen={show}
            onClose={() => setShow(false)}
            title="Select Meal Preference"
            onConfirm={() => setShow(false)}
            confirmButton="Ok"
            dismissible={false}
            showCancel={false}
          >
            <div className="font-mono">
              <p>Please select a meal preference to continue </p>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default ChoosePlan;
